import {
  Drawer,
  Descriptions,
  Tag,
  Table,
  Space,
  Button,
  Typography,
  Divider,
  Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useBatchOperations } from '@hooks/useWarehouse';
import type {
  StockItem,
  BatchOperationLog,
  BatchStatus,
  WarehouseOperationType,
} from '@/types/warehouse';

const { Text } = Typography;

interface BatchDrawerProps {
  mdItem: StockItem | null;
  mdOpen: boolean;
  mdOnClose: () => void;
  mdOnAdjust: (mdItem: StockItem) => void;
}

const STATUS_COLORS: Record<BatchStatus, string> = {
  Active: 'green',
  Reserved: 'blue',
  Blocked: 'orange',
  Recalled: 'red',
  Expired: 'default',
  Quarantine: 'purple',
  WrittenOff: 'default',
};

const STATUS_LABELS: Record<BatchStatus, string> = {
  Active: 'Активна',
  Reserved: 'Зарезервовано',
  Blocked: 'Заблоковано',
  Recalled: 'Відкликано',
  Expired: 'Прострочено',
  Quarantine: 'Карантин',
  WrittenOff: 'Списано',
};

const OPERATION_LABELS: Record<WarehouseOperationType, string> = {
  Receipt: 'Оприбуткування',
  Shipment: 'Відвантаження',
  Adjustment: 'Коригування',
  Transfer: 'Переміщення',
  Inventory: 'Інвентаризація',
  Recall: 'Відкликання',
};

const mdOperationColumns: ColumnsType<BatchOperationLog> = [
  {
    title: 'Дата',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 160,
    render: (mdValue: string) => dayjs(mdValue).format('DD.MM.YYYY HH:mm'),
  },
  {
    title: 'Операція',
    dataIndex: 'operationType',
    key: 'operationType',
    width: 140,
    render: (mdValue: WarehouseOperationType) => OPERATION_LABELS[mdValue] ?? mdValue,
  },
  {
    title: 'К-сть',
    dataIndex: 'quantity',
    key: 'quantity',
    width: 80,
    align: 'right',
  },
  {
    title: 'До',
    dataIndex: 'quantityBefore',
    key: 'quantityBefore',
    width: 60,
    align: 'right',
    render: (mdValue: number | null) => mdValue ?? '—',
  },
  {
    title: 'Після',
    dataIndex: 'quantityAfter',
    key: 'quantityAfter',
    width: 60,
    align: 'right',
    render: (mdValue: number | null) => mdValue ?? '—',
  },
  {
    title: 'Користувач',
    dataIndex: 'userName',
    key: 'userName',
    width: 140,
    render: (mdValue: string | null) => mdValue ?? '—',
  },
  {
    title: 'Причина',
    dataIndex: 'reason',
    key: 'reason',
    ellipsis: true,
    render: (mdValue: string | null) => mdValue ?? '—',
  },
];

export default function BatchDrawer({ mdItem, mdOpen, mdOnClose, mdOnAdjust }: BatchDrawerProps) {
  const {
    data: mdOperations,
    isLoading: mdOperationsLoading,
    isError: mdOperationsError,
  } = useBatchOperations(mdItem?.batchId);

  if (!mdItem) return null;

  const mdFormatDate = (mdDate: string | null): string => {
    if (!mdDate) return '—';
    return dayjs(mdDate).format('DD.MM.YYYY');
  };

  return (
    <Drawer
      title={`Партія: ${mdItem.batchNumber}`}
      open={mdOpen}
      onClose={mdOnClose}
      width={720}
      extra={
        <Space>
          <Button
            danger
            onClick={mdOnClose}
          >
            Заблокувати
          </Button>
          <Button type="primary" onClick={() => mdOnAdjust(mdItem)}>
            Скоригувати
          </Button>
        </Space>
      }
    >
      <Descriptions
        title="Ідентифікація"
        bordered
        size="small"
        column={2}
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label="SKU">{mdItem.sku}</Descriptions.Item>
        <Descriptions.Item label="Назва">{mdItem.productName}</Descriptions.Item>
        <Descriptions.Item label="Партія">{mdItem.batchNumber}</Descriptions.Item>
        <Descriptions.Item label="Серійний №">
          {mdItem.serialNumber ?? '—'}
        </Descriptions.Item>
        {/* riskClass / isSterile сервер у StockItemDto поки не надсилає */}
        <Descriptions.Item label="Клас ризику">{mdItem.riskClass ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Стерильний">
          {mdItem.isSterile === undefined ? (
            '—'
          ) : mdItem.isSterile ? (
            <Tag color="blue">Так</Tag>
          ) : (
            <Text type="secondary">Ні</Text>
          )}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        title="Дати"
        bordered
        size="small"
        column={2}
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label="Дата виробництва">
          {mdFormatDate(mdItem.manufactureDate)}
        </Descriptions.Item>
        <Descriptions.Item label="Термін придатності">
          {mdFormatDate(mdItem.expiryDate)}
        </Descriptions.Item>
        <Descriptions.Item label="Статус" span={2}>
          <Tag color={STATUS_COLORS[mdItem.status] ?? 'default'}>
            {STATUS_LABELS[mdItem.status] ?? mdItem.status}
          </Tag>
          {mdItem.isExpiringSoon && (
            <Tag color="warning" style={{ marginLeft: 8 }}>
              Спливає термін
            </Tag>
          )}
        </Descriptions.Item>
      </Descriptions>

      <Descriptions
        title="Склад"
        bordered
        size="small"
        column={2}
        style={{ marginBottom: 24 }}
      >
        <Descriptions.Item label="Локація">
          {mdItem.locationCode ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label="На складі">{mdItem.quantityOnHand}</Descriptions.Item>
        <Descriptions.Item label="Зарезервовано">{mdItem.quantityReserved}</Descriptions.Item>
        <Descriptions.Item label="Доступно">
          <Text strong>{mdItem.quantityAvailable}</Text>
        </Descriptions.Item>
      </Descriptions>

      <Divider />

      <Typography.Title level={5}>Історія операцій</Typography.Title>
      {mdOperationsLoading ? (
        <Spin />
      ) : mdOperationsError ? (
        <Text type="secondary">Історія операцій недоступна</Text>
      ) : (
        <Table
          columns={mdOperationColumns}
          dataSource={mdOperations?.slice(0, 10) ?? []}
          rowKey="id"
          size="small"
          pagination={false}
        />
      )}
    </Drawer>
  );
}
